import type { PageHelp } from "../../help/types";

export const benefitsHelp: PageHelp = {
  title: "Benefits",
  intro:
    "Every benefit package your employer offers you and your dependants. Here you see what you are enrolled in, what you can still enrol in, and you enrol in, upgrade or cancel a package.",
  sections: [
    "Finding a benefit",
    "Reading a benefit card",
    "Benefit details",
    "Enrol in a package",
    "Cancel an enrolment",
  ],
  contexts: {
    "filter-popover": 'Click "Filter" at the right end of the tab row.',
    "enrolled-or-eligible-tab": 'Select the "Enrolled" or "Eligible" tab.',
    "details-dialog": 'Click "View details" at the bottom of a benefit card.',
    "enroll-dialog": 'Open the "Eligible" tab and click "Enrol" (or "Upgrade") on a benefit card.',
    "cancel-dialog": 'Open the "Enrolled" tab and click "Cancel" on a benefit card.',
  },
  tips: [
    "The person tags above the cards decide whose benefits you see. Everything on this page — the tabs, the filters and the Enrol and Cancel buttons — works for the person who is selected.",
    'Some packages need approval. An enrolment or a cancellation of such a package is shown as "In review" until it is approved; if it is rejected, "View details" turns red and shows the reason.',
    "Dates are checked again on the server: an enrolment date must lie within your eligibility period and not in the past, and a cancellation date must not lie before the earliest possible cancellation date.",
  ],
  steps: [
    {
      selector: '[data-help="benefits-content-tabs"]',
      title: "Switch between your benefit lists",
      body: '"All" shows everything at once, "Enrolled" the packages you already have, "Eligible" the packages you can still enrol in, and "Expired" packages whose validity period has ended. The page opens on "Enrolled".',
      section: "Finding a benefit",
    },
    {
      selector: '[data-help="benefits-person-select"]',
      title: "Choose whose benefits you see",
      body: "One tag for you and one for each dependant registered in your profile. Click a tag to show that person's packages; the selected tag is highlighted. Dependants are added on the Profile page.",
      section: "Finding a benefit",
    },
    {
      selector: '[data-help="benefits-filter-button"]',
      title: "Open the filters",
      body: "Opens a small panel with the category and status filters for the tab you are on. Each tab remembers its own filter selection.",
      section: "Finding a benefit",
    },
    {
      selector: '[data-help="benefits-category-filter"]',
      title: "Filter benefits by category",
      body: 'Tick the categories you want to see. All categories are selected at first; "Clear all" hides every card until you tick at least one category again, and "Select all" brings them all back.',
      section: "Finding a benefit",
      context: "filter-popover",
    },
    {
      selector: '[data-help="benefits-status-filter"]',
      title: "Show only packages in review",
      body: 'Tick "In review" to see only the enrolments or cancellations that are still waiting for approval. Untick it to see all packages again.',
      section: "Finding a benefit",
      context: "filter-popover",
    },
    {
      selector: '[data-help="benefits-show-expired"]',
      title: "Include expired packages",
      body: 'Switch it on to also list packages whose validity period has ended. It is only offered on the "Enrolled" and "Eligible" tabs; "All" and "Expired" include expired packages anyway.',
      section: "Finding a benefit",
      context: "enrolled-or-eligible-tab",
    },
    {
      selector: '[data-help="benefits-card"]',
      title: "Read a benefit card",
      body: "One card per package: the category icon, the category and package name, and a short description. Hover over the name or the description to read the full text when it is cut off.",
      section: "Reading a benefit card",
      optional: true,
    },
    {
      selector: '[data-help="benefits-card-price"]',
      title: "See what you pay",
      body: "Your own share of the price, the currency and how often it is paid. When the split between you and your employer is set as a percentage, this is already your amount. Hover over it for the full wording.",
      section: "Reading a benefit card",
      optional: true,
    },
    {
      selector: '[data-help="benefits-card-status"]',
      title: "Check the package status",
      body: '"Eligible" means you can enrol, "Enrolled" that you have the package, "In review" that an enrolment or cancellation waits for approval, and "Cancelled" or "Expired" that the package has ended for you.',
      section: "Reading a benefit card",
      optional: true,
    },
    {
      selector: '[data-help="benefits-card-details"]',
      title: "Open the package details",
      body: "Opens the full description, the dates and the package documents. The link turns red with a warning icon when an enrolment or cancellation was rejected — open it to read the reason.",
      section: "Reading a benefit card",
      optional: true,
    },
    {
      selector: '[data-help="benefits-details-validity"]',
      title: "Check the validity period and price",
      body: "The first pill shows from when to when the package is valid; the second repeats your share of the price and the payment frequency.",
      section: "Benefit details",
      context: "details-dialog",
    },
    {
      selector: '[data-help="benefits-details-rejection"]',
      title: "Read why a request was rejected",
      body: "Shown only when your enrolment or cancellation was rejected. It says which request was rejected and the comment the reviewer left. You can send a new request from the card.",
      section: "Benefit details",
      context: "details-dialog",
      optional: true,
    },
    {
      selector: '[data-help="benefits-details-dates"]',
      title: "Check your eligibility and cancellation dates",
      body: "For a package you can enrol in, the period in which you are eligible; for an enrolled package, the date you were enrolled on. On the right is the earliest date from which the package can be cancelled.",
      section: "Benefit details",
      context: "details-dialog",
    },
    {
      selector: '[data-help="benefits-details-documents"]',
      title: "Open a package document",
      body: "Terms, conditions and other documents attached to this package. Click a document to open it in a new browser tab.",
      section: "Benefit details",
      context: "details-dialog",
      optional: true,
    },
    {
      selector: '[data-help="benefits-card-enroll"]',
      title: "Enrol in a package",
      body: '"Enrol" opens the enrolment form for the selected person. It reads "Upgrade" when that person already has another package of the same benefit; upgrading replaces the current package of that benefit. The button is disabled while another package of the same benefit is still in review.',
      section: "Enrol in a package",
      optional: true,
    },
    {
      selector: '[data-help="benefits-enroll-date"]',
      title: "Choose the enrolment date",
      body: "The day the package should start. It cannot be in the past, and the server only accepts a date within your eligibility period (shown in the package details).",
      section: "Enrol in a package",
      context: "enroll-dialog",
    },
    {
      selector: '[data-help="benefits-enroll-cancellation-date"]',
      title: "See the earliest cancellation date",
      body: "Read-only. Calculated from the enrolment date and the package rules: the earliest day from which you will be able to cancel this package. It updates when you change the enrolment date.",
      section: "Enrol in a package",
      context: "enroll-dialog",
    },
    {
      selector: '[data-help="benefits-enroll-insurer-consent"]',
      title: "Give the insurer's consent",
      body: 'Shown when the insurer needs your consent. Depending on the package you follow a link to the insurer, download a form and send it on paper or upload it, or tick "I agree" under the insurer\'s text — that tick is required before you can enrol. Not asked again for an upgrade or when the consent is already on file.',
      section: "Enrol in a package",
      context: "enroll-dialog",
      optional: true,
    },
    {
      selector: '[data-help="benefits-enroll-consent-upload"]',
      title: "Upload the signed consent form",
      body: "Shown when the insurer's form has to be uploaded as a scan. Download the form above, sign it, then upload one or more files here; the bin icon removes a file you added by mistake.",
      section: "Enrol in a package",
      context: "enroll-dialog",
      optional: true,
    },
    {
      selector: '[data-help="benefits-enroll-sensitive-consent"]',
      title: "Agree to the processing of sensitive data",
      body: "Shown until you have once given GrECo your consent to process sensitive data, such as health data. The link opens the consent text; tick the box to agree. Once given, it is not asked again for later enrolments.",
      section: "Enrol in a package",
      context: "enroll-dialog",
      optional: true,
    },
    {
      selector: '[data-help="benefits-enroll-salary-deduction"]',
      title: "Provide the salary deduction consent",
      body: "Shown for a package paid by salary deduction when no signed consent is on file for it yet. Download the template (when your employer has provided one), sign it and upload it here.",
      section: "Enrol in a package",
      context: "enroll-dialog",
      optional: true,
    },
    {
      selector: '[data-help="benefits-enroll-submit"]',
      title: "Send the enrolment",
      body: 'Sends the enrolment (or the upgrade). If a required consent is missing, nothing is sent and the button stays disabled until you give it. When the package needs approval, the card shows "In review" until it is decided.',
      section: "Enrol in a package",
      context: "enroll-dialog",
    },
    {
      selector: '[data-help="benefits-card-cancel"]',
      title: "Cancel a package",
      body: "Opens the cancellation form. The button is shown only on an enrolled package that is not already cancelled, and only when the package can still be cancelled before it ends.",
      section: "Cancel an enrolment",
      optional: true,
    },
    {
      selector: '[data-help="benefits-cancel-date"]',
      title: "Choose the cancellation date",
      body: "The last day of the package for you. It cannot be in the past or before the earliest possible cancellation date shown below; until it is valid, the cancel button stays disabled.",
      section: "Cancel an enrolment",
      context: "cancel-dialog",
    },
    {
      selector: '[data-help="benefits-cancel-reason"]',
      title: "Give a reason for the cancellation",
      body: "Required, up to 4000 characters. The reason is passed on to whoever processes the cancellation.",
      section: "Cancel an enrolment",
      context: "cancel-dialog",
    },
    {
      selector: '[data-help="benefits-cancel-possibility"]',
      title: "See the earliest cancellation date",
      body: "Read-only. The earliest day the package rules allow you to cancel from. Pick a cancellation date on or after it.",
      section: "Cancel an enrolment",
      context: "cancel-dialog",
    },
    {
      selector: '[data-help="benefits-cancel-submit"]',
      title: "Confirm the cancellation",
      body: 'Sends the cancellation. It is enabled once a valid date and a reason are filled in. When the package needs approval for cancellation, the card shows "In review" until it is decided.',
      section: "Cancel an enrolment",
      context: "cancel-dialog",
    },
  ],
};
